import { ConnectError, Interceptor } from '@bufbuild/connect';

export const MAINTENANCE_PATH = '/maintenance';
export const MAINTENANCE_EVENT = 'geneo:service-maintenance';

const STORAGE_KEY = 'geneo.serviceMaintenance';

const DEFAULT_MESSAGE = 'Application is under maintenance.';
const BYPASS_HEADER = 'X-Maintenance-Bypass';

export interface ServiceMaintenanceDetails {
  status: number;
  code?: string;
  message: string;
  retryAfterSeconds?: number;
}

let redirectScheduled = false;
let handledByRouter = false;

export function readMaintenanceDetails(): ServiceMaintenanceDetails {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { status: 503, message: DEFAULT_MESSAGE };
    }
    const parsed = JSON.parse(raw) as Partial<ServiceMaintenanceDetails>;
    return {
      status: 503,
      code: typeof parsed.code === 'string' ? parsed.code : undefined,
      message:
        typeof parsed.message === 'string' && parsed.message.trim()
          ? parsed.message
          : DEFAULT_MESSAGE,
      retryAfterSeconds:
        typeof parsed.retryAfterSeconds === 'number'
          ? parsed.retryAfterSeconds
          : undefined,
    };
  } catch {
    return { status: 503, message: DEFAULT_MESSAGE };
  }
}

export function clearMaintenanceRedirectGuard() {
  redirectScheduled = false;
}

export function subscribeToMaintenanceRedirect(handler: () => void) {
  const listener = () => {
    handledByRouter = true;
    handler();
  };
  window.addEventListener(MAINTENANCE_EVENT, listener);
  return () => window.removeEventListener(MAINTENANCE_EVENT, listener);
}

function parseMaintenanceBody(data: unknown): ServiceMaintenanceDetails {
  if (!data || typeof data !== 'object') {
    return { status: 503, message: DEFAULT_MESSAGE };
  }
  const body = data as Record<string, unknown>;
  return {
    status: 503,
    code: typeof body.code === 'string' ? body.code : undefined,
    message:
      typeof body.message === 'string' && body.message.trim()
        ? body.message
        : DEFAULT_MESSAGE,
    retryAfterSeconds:
      typeof body.retryAfterSeconds === 'number'
        ? body.retryAfterSeconds
        : undefined,
  };
}

/** Non-empty `NX_MAINTENANCE_BYPASS_KEY`, or undefined when unset or blank. */
export function maintenanceBypassKey(): string | undefined {
  const value = process.env.NX_MAINTENANCE_BYPASS_KEY;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Copies request headers and sets `X-Maintenance-Bypass` when a bypass key is configured.
 * Existing header values are left as-is.
 */
export function withMaintenanceBypass(
  input: RequestInfo | URL,
  init?: RequestInit
): RequestInit | undefined {
  const key = maintenanceBypassKey();
  if (!key) return init;

  const headers = new Headers(
    input instanceof Request ? input.headers : undefined
  );
  if (init?.headers) {
    new Headers(init.headers).forEach((value, name) => headers.set(name, value));
  }
  if (!headers.has(BYPASS_HEADER)) {
    headers.set(BYPASS_HEADER, key);
  }
  return { ...init, headers };
}

function redirectToMaintenance(details: ServiceMaintenanceDetails) {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === MAINTENANCE_PATH) return;
  if (redirectScheduled) return;
  redirectScheduled = true;
  handledByRouter = false;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(details));
  } catch {
    // sessionStorage can be unavailable; the page still has a default message
  }
  window.dispatchEvent(new CustomEvent(MAINTENANCE_EVENT));
  window.setTimeout(() => {
    if (
      !handledByRouter &&
      window.location.pathname !== MAINTENANCE_PATH
    ) {
      window.location.assign(MAINTENANCE_PATH);
    }
  }, 50);
}

export function isMaintenancePayload(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const body = data as Record<string, unknown>;
  return body.status === 503 || body.code === 'SERVICE_MAINTENANCE';
}

export function redirectToMaintenanceFromBody(data: unknown) {
  redirectToMaintenance(parseMaintenanceBody(data));
}

function backendBaseUrl() {
  return process.env.NX_GENEO_BACKEND_URL || 'https://dev2-api.geneo.in';
}

function sameBackend(url: URL, backendBaseUrlValue: string) {
  const base = backendBaseUrlValue.replace(/\/+$/, '');
  const href = url.href.replace(/\/+$/, '');
  return href === base || url.href.startsWith(`${base}/`);
}

export async function maybeRedirectForMaintenance(response: Response) {
  if (response.status !== 503) return;
  if (typeof window === 'undefined') return;
  if (window.location.pathname === MAINTENANCE_PATH) return;
  if (redirectScheduled) return;
  // Claim the redirect before reading the body so parallel 503s share one navigation.
  redirectScheduled = true;
  handledByRouter = false;
  let details: ServiceMaintenanceDetails = {
    status: 503,
    message: DEFAULT_MESSAGE,
  };
  try {
    details = parseMaintenanceBody(await response.clone().json());
  } catch {
    // Non-JSON 503 still sends the user to the maintenance page.
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(details));
  } catch {
    // sessionStorage can be unavailable; the page still has a default message
  }
  window.dispatchEvent(new CustomEvent(MAINTENANCE_EVENT));
  window.setTimeout(() => {
    if (!handledByRouter && window.location.pathname !== MAINTENANCE_PATH) {
      window.location.assign(MAINTENANCE_PATH);
    }
  }, 50);
}

export function isBackendApiRequest(
  input: RequestInfo | URL,
  backendBaseUrl?: string
): boolean {
  try {
    const raw =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.href
        : input.url;
    const url = new URL(raw, window.location.origin);
    if (backendBaseUrl && sameBackend(url, backendBaseUrl)) return true;
    if (url.pathname === '/pal' || url.pathname.startsWith('/pal/')) return true;
    if (url.pathname.includes('.service.')) return true;
    return false;
  } catch {
    return false;
  }
}

/** Fetch wrapper for direct backend calls (axios does not use this). */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const nextInit = isBackendApiRequest(input, backendBaseUrl())
    ? withMaintenanceBypass(input, init)
    : init;
  const response = await fetch(input, nextInit);
  if (
    response.status === 503 &&
    isBackendApiRequest(input, backendBaseUrl())
  ) {
    await maybeRedirectForMaintenance(response);
  }
  return response;
}

let fetchPatchInstalled = false;

export function installMaintenanceFetchPatch(
  isApiRequest: (input: RequestInfo | URL) => boolean
) {
  if (fetchPatchInstalled || typeof window === 'undefined') return;
  fetchPatchInstalled = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const nextInit = isApiRequest(input)
      ? withMaintenanceBypass(input, init)
      : init;
    const response = await originalFetch(input, nextInit);
    if (response.status === 503 && isApiRequest(input)) {
      void maybeRedirectForMaintenance(response);
    }
    return response;
  };
}

export const maintenanceInterceptor: Interceptor = (next) => async (req) => {
  const key = maintenanceBypassKey();
  if (key && !req.header.has(BYPASS_HEADER)) {
    req.header.set(BYPASS_HEADER, key);
  }
  try {
    return await next(req);
  } catch (error) {
    if (error instanceof ConnectError) {
      const raw = error.rawMessage?.trim() ?? '';
      if (raw.includes('SERVICE_MAINTENANCE')) {
        let body: unknown = { message: raw };
        if (raw.startsWith('{')) {
          try {
            body = JSON.parse(raw);
          } catch {
            body = { message: raw, code: 'SERVICE_MAINTENANCE' };
          }
        }
        redirectToMaintenanceFromBody(body);
      }
    }
    throw error;
  }
};
